(ns pfo.staging.component
  (:require
    [reagent.core :as reagent :refer [atom]]
    [pfo.controller :as controller :refer [subscribe]]
    [pfo.assignment.component :as assignment]
    [pfo.staging.aark :as aark]
    [pfo.util :as util]))

;; -----------------------------------------------------------------------------
;; Navigation into the full 3-pane "Unfiled" review workspace on the Documents
;; page. The dashboard preview is just an at-a-glance entry point; the real
;; reviewing happens in the workspace (see pfo.staging.workspace).
;; -----------------------------------------------------------------------------
(defn unfiled-workspace-url
  "Hash URL of the standalone Unfiled review workspace, optionally focusing a
  specific staged document."
  ([] "#/document/unfiled")
  ([document-id] (str "#/document/unfiled?focus=" document-id)))

;; Staff document review queue (#2851). Renders the pending staged submissions
;; returned by GET /api/v1/document/staging/queue/ and lets a reviewer claim
;; or file each one. Wired to the real endpoints via the
;; :staging-queue controller.
;;
;; Real queue row shape: {:id document_id :submission_id :family_id :status :name
;;   :extension :content_type :uploader_id :uploader_name :submitted_at
;;   :classification_status :classification_proposal} — the last two are the AARK
;;   first-pass classification (#2854 Stage 3), normalized via pfo.staging.aark.

(defn- reload-queue []
  (controller/dispatch :staging-queue :load nil))

(defn status-label [status]
  (case status
    "submitted"    [:span.staging-status.staging-status--submitted "Submitted"]
    "under_review" [:span.staging-status.staging-status--review "Under review"]
    "filed"        [:span.staging-status.staging-status--filed "Filed"]
    "rejected"     [:span.staging-status.staging-status--rejected "Rejected"]
    [:span status]))

(defn format-date [date-string]
  (when (not-empty date-string)
    (util/date-reformat date-string)))

(defn time-ago
  "Compact relative time for an RFC3339 timestamp, e.g. \"2h ago\", \"3d ago\".
  Falls back to the reformatted date for anything older than ~30 days."
  [date-string]
  (when (not-empty date-string)
    (let [then (.getTime (js/Date. date-string))
          now  (.getTime (js/Date.))
          secs (max 0 (js/Math.floor (/ (- now then) 1000)))
          mins (js/Math.floor (/ secs 60))
          hrs  (js/Math.floor (/ mins 60))
          days (js/Math.floor (/ hrs 24))]
      (cond
        (< secs 60)  "just now"
        (< mins 60)  (str mins "m ago")
        (< hrs 24)   (str hrs "h ago")
        (< days 30)  (str days "d ago")
        :else        (format-date date-string)))))

(defn uploaded-meta
  "\"from {uploader} · {relative time}\" line shown under a document name."
  [item]
  [:span.staging-doc-meta
   "from " (:uploader_name item)
   (when-let [ago (time-ago (:submitted_at item))]
     [:span " · " ago])])

(defn document-link
  "Opens the staged document in a new tab via a short-lived session-less preview
  URL (staged docs have no collection, so the normal document route can't load
  them). A blank tab is opened synchronously here to avoid popup blockers, then
  the controller points it at the URL once fetched."
  [item]
  [:a {:href "#"
       :on-click (fn [e]
                   (.preventDefault e)
                   (let [w (js/window.open "" "_blank")]
                     (controller/dispatch :staging-queue :preview
                       {:document-id (:id item) :win w})))}
   (:name item)
   (when-let [ext (:extension item)]
     [:span " ." ext])])

(defn- file-prompt
  "Inline filing form — the reviewer selects one or more target collections
  (reusing the document collection-assignment picker, scoped to the
  submission's family) and optionally overrides the name, then files the
  document. On success the queue is reloaded so the filed row drops out.

  Seam for #2854 Stage 3: when the queue starts returning an AARK
  classification proposal, pre-fill the assignments/name atoms from it here."
  [item on-done]
  (let [assignments           (atom (assignment/get-initial-ui-assignments nil))
        errors                (atom {})
        assignments-to-delete (atom #{})
        group-id              (atom (:family_id item))
        primary-collection-id (atom nil)
        include-inactive      (atom false)
        name                  (atom (:name item))]
    (fn [item on-done]
      [:div.staging-file-form {:style {:padding "0.5em 0"}}
       [assignment/assignments-edit-notable-redesign
        {:include-inactive-atom include-inactive}
        assignments errors nil assignments-to-delete group-id primary-collection-id]
       [:div.o-margin-elements--medium
        [:label "Name "]
        [:input {:type "text"
                 :value @name
                 :style {:padding "0.25em 0.5em" :font-size "0.8rem"}
                 :on-change #(reset! name (.. % -target -value))}]]
       [:div {:style {:display "flex" :gap "0.5em" :align-items "center"}}
        [:button.c-button--small
         {:disabled (empty? (assignment/get-collection-ids @assignments))
          :on-click #(controller/dispatch :staging-queue :file
                       {:document-id        (:id item)
                        :collection-ids     (assignment/get-collection-ids @assignments)
                        :main-collection-id @primary-collection-id
                        :name               (when (not= @name (:name item)) @name)
                        :on-success         (fn [_] (reload-queue) (on-done))})}
         "File"]
        [:a {:href "#" :on-click (fn [e] (.preventDefault e) (on-done))} "Cancel"]]])))

(defn- queue-row-actions [item]
  (let [filing (atom false)]
    (fn [item]
      (let [document-id (:id item)
            status      (:status item)]
        (cond
          @filing
          [:td [file-prompt item #(reset! filing false)]]

          (= status "submitted")
          [:td
           [:button.c-button--small
            {:on-click #(controller/dispatch :staging-queue :claim
                          {:document-id document-id
                           :on-success (fn [_] (reload-queue))
                           ;; Lost claim race (#2928): reload so the row shows
                           ;; who holds it; rethrow for the default error toast.
                           :on-error   (fn [error-response]
                                         (reload-queue)
                                         (throw error-response))})}
            "Review"]]

          (= status "under_review")
          [:td
           [:button.c-button--small
            {:on-click #(reset! filing true)}
            "File"]]

          :else
          [:td])))))

(defn staging-queue-list []
  (let [items (subscribe :staging-queue)]
    (controller/dispatch :staging-queue :load nil)
    (fn []
      (cond
        (nil? @items)
        [:p.o-media "Loading..."]

        (empty? @items)
        [:p.o-media "No documents pending review."]

        :else
        [:table.messages.t-table
         [:thead
          [:tr
           [:th "Document"]
           [:th "Submitted by"]
           [:th "Status"]
           [:th "Submitted"]
           [:th ""]]]
         [:tbody
          (doall
            (for [item @items]
              ^{:key (:id item)}
              [:tr
               [:td [document-link item]]
               [:td (:uploader_name item)]
               [:td [status-label (:status item)]]
               [:td (format-date (:submitted_at item))]
               [queue-row-actions item]]))]]))))

(defn my-uploads-list
  "The novice user's own uploads and where each one is in review."
  []
  (let [items (subscribe :my-uploads)]
    (controller/dispatch :my-uploads :load nil)
    (fn []
      (cond
        (nil? @items)
        [:p.o-media "Loading..."]

        (= :error @items)
        [:p.o-media "Your uploads could not be loaded."]

        (empty? @items)
        [:p.o-media "You have not uploaded any documents."]

        :else
        [:table.messages.t-table
         [:thead
          [:tr
           [:th "Document"]
           [:th "Status"]
           [:th "Uploaded"]]]
         [:tbody
          (doall
            (for [item @items]
              ^{:key (:id item)}
              [:tr
               [:td (:name item)]
               [:td [status-label (:status item)]
                (when-let [reason (not-empty (:rejection_reason item))]
                  [:div.staging-doc-meta reason])]
               [:td (format-date (:created_at item))]]))]]))))

;; =============================================================================
;; Dashboard surface — compact "Documents to Process" preview (design 1)
;; =============================================================================
;; An at-a-glance list of the first few unfiled submissions awaiting review.
;; The AARK proposal (#2854 Stage 3) carries only ids; the staging queue-read
;; denormalizes them (resolveProposalLabels) into a collection breadcrumb
;; (:proposal-collections, document_collections-shaped) and a category name
;; (:category-name) so this preview can show the suggested filing at a glance —
;; the same breadcrumb the document list renders. Caveat notes are NOT shown here
;; (they stay in the full workspace review). Each row and the footer link
;; deep-link into the full Unfiled review workspace on the Documents page.

(def ^:private dashboard-preview-limit 5)

(defn- proposal-date-str
  "Compact display string for the AARK-proposed application date, from whatever
  parts AARK gave (full date, year+quarter, or just a year), or nil."
  [{:keys [year quarter month day]}]
  (cond
    (and year month day) (str month "/" day "/" (mod year 100))
    (and year quarter)   (str "Q" quarter " " year)
    year                 (str year)
    :else                nil))

(defn documents-to-process-tab-label
  "Dashboard tab label with a live count badge. Loads the queue on mount so the
  badge populates even before the tab is opened."
  []
  (let [items (subscribe :staging-queue)]
    (controller/dispatch :staging-queue :load nil)
    (fn []
      [:span
       [:img.tab-header {:src "/static/img/icons/document.svg"}]
       [:span.name "Documents to Process"]
       (when-let [n (some-> @items count)]
         (when (pos? n)
           [:span.tab-count {:style {:margin-left "0.4em"}} n]))])))

(defn documents-icon
  "Top-bar Documents icon with a pending-count badge, mirroring the Messages
  chat icon (im/chat-icon, #2928 entrypoint). Clicking deep-links into the
  Unfiled review workspace. Loads the queue on mount so the badge populates on
  any page; shares the :staging-queue store with the dashboard tab label, so
  filing elsewhere refreshes the count. Callers gate rendering on
  auth-model/can-access-document-review?."
  []
  (let [items (subscribe :staging-queue)]
    (controller/dispatch :staging-queue :load nil)
    (fn []
      [:a.c-chat-icon.c-doc-icon {:href (unfiled-workspace-url)}
       [:img.svg-icon--s {:src "/static/img/icons/document-white.svg" :alt "Documents to process"}]
       (when-let [n (some-> @items count)]
         (when (pos? n)
           [:span.c-chat-icon__badge n]))])))

(defn documents-to-process-preview []
  (let [items (subscribe :staging-queue)]
    (controller/dispatch :staging-queue :load nil)
    (fn []
      (let [rows @items]
        (cond
          (nil? rows)
          [:p.o-media "Loading..."]

          (empty? rows)
          [:p.o-media "No documents awaiting review."]

          :else
          [:div.staging-preview
           [:table.t-table.staging-preview__table
            [:thead
             [:tr
              [:th "Document"]
              [:th "Collection"]
              [:th "Category"]
              [:th "Applies to"]
              [:th ""]]]
            [:tbody
             (doall
               (for [item (take dashboard-preview-limit rows)]
                 (let [proposal             (aark/proposal-for item)
                       proposal-collections (:proposal-collections proposal)
                       category-name        (:category-name proposal)]
                   ^{:key (:id item)}
                   [:tr
                    [:td
                     [:div.staging-doc-name (:name item)]
                     (when-let [cn (:custom-name proposal)]
                       [:div.staging-classification (str "AARK name: " cn)])
                     [uploaded-meta item]]
                    ;; Collection / Category / Applies-to: backend-resolved labels
                    ;; (breadcrumb + category name); dashes when AARK produced
                    ;; nothing. .category / .date give these cells the same
                    ;; centered alignment as their centered <th> (the .t-table
                    ;; convention the document list uses); the wide Collection
                    ;; breadcrumb stays left, like td.assignments there.
                    [:td (if (seq proposal-collections)
                           [assignment/assignment-list proposal-collections]
                           "—")]
                    [:td.category (or category-name "—")]
                    [:td.date (or (proposal-date-str (:date proposal)) "—")]
                    [:td
                     [:a.c-button--outlined.c-button--small.staging-review-link
                      {:href (unfiled-workspace-url (:id item))}
                      "Review →"]]])))]]
           [:div.staging-preview__footer
            [:a {:href (unfiled-workspace-url)}
             (str "View " (count rows) " Unfiled Document" (when (not= 1 (count rows)) "s") " →")]]])))))
